import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import TutorialButton from './common/TutorialButton'; // Import TutorialButton
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { tratarErroAPI, logarRequisicao, logarSucesso } from '../utils/errorHandler';
import '../styles/desenho.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';

const ManutencaoManager = () => {
  const navigate = useNavigate();
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [formData, setFormData] = useState({
    id_manutencao: '',
    id_escola: '',
    id_tp_manutencao: '',
    id_status: '',
    descr_manutencao: '',
    data_solicitacao: '',
    usuario_responsavel: '',
    nome_campanha: '',
    id_gerencia: ''
  });
  const [apiStatus, setApiStatus] = useState('checking');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [escolaSearch, setEscolaSearch] = useState('');
  const [escolas, setEscolas] = useState([]);
  const [tiposManutencao, setTiposManutencao] = useState([]);
  const [status, setStatus] = useState([]);
  const [gerencias, setGerencias] = useState([]);
  const [camposComErro, setCamposComErro] = useState([]);

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);

  // Filtrar escolas com base na busca
  const escolasFiltradas = useMemo(() => {
    if (!escolaSearch) return escolas;
    return escolas.filter(escola =>
      escola.nome_escola?.toLowerCase().includes(escolaSearch.toLowerCase()) ||
      escola.id_escola?.toString().includes(escolaSearch) ||
      escola.codigo_inep?.toString().includes(escolaSearch)
    );
  }, [escolas, escolaSearch]);

  const mostrarMensagem = useCallback((tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  }, []);

  const fetchEscolas = useCallback(async () => {
    try {
      const response = await api.get('/escola');
      setEscolas(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar escolas: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const fetchTiposManutencao = useCallback(async () => {
    try {
      const response = await api.get('/tipo-manutencao');
      setTiposManutencao(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar tipos de manutenção: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await api.get('/status');
      setStatus(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar status: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const fetchGerencias = useCallback(async () => {
    try {
      const response = await api.get('/gerencia');
      setGerencias(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar gerências: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const verificarAPI = useCallback(async () => {
    try {
      setApiStatus('checking');
      await api.get('/manutencao');
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('error');
      mostrarMensagem('error', `Erro de conexão: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const carregarDados = useCallback(async (showMessage = true) => {
    try {
      setLoading(true);
      const response = await api.get('/manutencao');
      setDados(response.data.data || []);
      setLastUpdate(new Date());
      if (showMessage) {
        mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
      }
    } catch (error) {
      mostrarMensagem('error', error.message);
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    verificarAPI();
    fetchEscolas();
    fetchTiposManutencao();
    fetchStatus();
    fetchGerencias();
    carregarDados();
  }, [verificarAPI, fetchEscolas, fetchTiposManutencao, fetchStatus, fetchGerencias, carregarDados]);

  const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
    logarRequisicao('Atualizar', 'manutenção', { id, dadosAtualizacao });
    try {
      const response = await api.put(`/manutencao/${id}`, dadosAtualizacao);
      logarSucesso('Atualizar', 'manutenção', response.data);
      if (response && response.data) {
        if (tabulatorRef.current) {
          tabulatorRef.current.updateData([response.data.data]);
        }
        mostrarMensagem('success', 'Registro atualizado com sucesso!');
        carregarDados(false);
      } else {
        mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
      }
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'atualizar', 'manutenção');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem, carregarDados]);

  const excluirRegistro = useCallback(async (id) => {
    logarRequisicao('Excluir', 'manutenção', { id });
    try {
      const response = await api.delete(`/manutencao/${id}`);
      logarSucesso('Excluir', 'manutenção', response.data);
      setDados(currentData => currentData.filter(item => item.id_manutencao !== id));
      mostrarMensagem('success', 'Registro excluído com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'excluir', 'manutenção');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const colunas = useMemo(() => {
    const escolasNomes = escolas.reduce((acc, item) => ({ ...acc, [item.id_escola]: item.nome_escola }), {});
    const tiposManutencaoNomes = tiposManutencao.reduce((acc, item) => ({ ...acc, [item.id_tp_manutencao]: item.descr_tp_manutencao }), {});
    const statusNomes = status.reduce((acc, item) => ({ ...acc, [item.id_status]: item.descr_status }), {});
    const gerenciasNomes = gerencias.reduce((acc, item) => ({ ...acc, [item.id_gerencia]: item.nome_gerencia }), {});

    return [
      {
        title: 'Salvar',
        field: 'salvar',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          atualizarRegistro(data.id_manutencao, data);
        },
        headerSort: false
      },
      {
        title: 'Excluir',
        field: 'excluir',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          if (window.confirm(`Deseja realmente excluir o registro "${data.id_manutencao}"?`)) {
            excluirRegistro(data.id_manutencao);
          }
        },
        headerSort: false
      },
      {
        title: 'ID Manutenção',
        field: 'id_manutencao',
        width: 150,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar ID...',
        sorter: 'string'
      },
      {
        title: 'Escola',
        field: 'id_escola',
        width: 250,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar Escola...',
        sorter: 'string',
        formatter: (cell) => escolasNomes[cell.getValue()] || cell.getValue(),
        editor: 'list',
        editorParams: { values: escolasNomes }
      },
      {
        title: 'Tipo Manutenção',
        field: 'id_tp_manutencao',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar Tipo...',
        sorter: 'string',
        formatter: (cell) => tiposManutencaoNomes[cell.getValue()] || cell.getValue(),
        editor: 'list',
        editorParams: { values: tiposManutencaoNomes }
      },
      {
        title: 'Status',
        field: 'id_status',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar Status...',
        sorter: 'string',
        formatter: (cell) => statusNomes[cell.getValue()] || cell.getValue(),
        editor: 'list',
        editorParams: { values: statusNomes }
      },
      {
        title: 'Descrição Manutenção',
        field: 'descr_manutencao',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar descrição...',
        sorter: 'string',
        editor: 'input'
      },
      {
        title: 'Data Solicitação',
        field: 'data_solicitacao',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar data...',
        sorter: 'date',
        editor: (cell, onRendered, success, cancel) => {
          const input = document.createElement("input");
          input.setAttribute("type", "text");
          input.setAttribute("placeholder", "DD/MM/YYYY");
          input.style.padding = "4px";
          input.style.width = "100%";
          input.style.boxSizing = "border-box";

          const value = cell.getValue();
          if (value) {
            try {
              const date = new Date(value);
              if (!isNaN(date.getTime())) {
                const day = String(date.getUTCDate()).padStart(2, '0');
                const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                const year = date.getUTCFullYear();
                input.value = `${day}/${month}/${year}`;
              } else {
                input.value = value;
              }
            } catch (e) {
              input.value = value;
            }
          }

          onRendered(() => {
            input.focus();
            input.style.height = "100%";
          });

          const handleDateInput = (e) => {
            const input = e.target;
            let value = input.value.replace(/\D/g, '');
            let formattedValue = '';
            if (value.length > 0) {
              formattedValue = value.substring(0, 2);
            }
            if (value.length > 2) {
              formattedValue += '/' + value.substring(2, 4);
            }
            if (value.length > 4) {
              formattedValue += '/' + value.substring(4, 8);
            }
            input.value = formattedValue;
          };

          input.addEventListener('input', handleDateInput);

          const onChange = () => {
            if (input.value && input.value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
              const [day, month, year] = input.value.split('/');
              success(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
            } else {
              success(input.value);
            }
          };

          input.addEventListener("change", onChange);
          input.addEventListener("blur", onChange);

          return input;
        },
        formatter: (cell) => {
          const value = cell.getValue();
          if (!value) return '';
          try {
            const date = new Date(value);
            if (isNaN(date.getTime())) return value;
            const day = String(date.getUTCDate()).padStart(2, '0');
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const year = date.getUTCFullYear();
            return `${day}/${month}/${year}`;
          } catch (e) {
            return value;
          }
        }
      },
      {
        title: 'Usuário Responsável',
        field: 'usuario_responsavel',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar usuário...',
        sorter: 'string',
        editor: 'input'
      },
      {
        title: 'Nome Campanha',
        field: 'nome_campanha',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar nome...',
        sorter: 'string',
        editor: 'input'
      },
      {
        title: 'Gerência',
        field: 'id_gerencia',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar Gerência...',
        sorter: 'string',
        formatter: (cell) => gerenciasNomes[cell.getValue()] || cell.getValue(),
        editor: 'list',
        editorParams: { values: gerenciasNomes }
      }
    ];
  }, [escolas, tiposManutencao, status, gerencias, atualizarRegistro, excluirRegistro]);

  useEffect(() => {
    if (apiStatus === 'connected') {
      carregarDados(false);
    }
  }, [apiStatus, carregarDados]);

  const inicializarTabulator = useCallback(() => {
    if (!tableRef.current) return;
    if (tabulatorRef.current) tabulatorRef.current.destroy();

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: dados,
      columns: colunas,
      layout: 'fitData',
      pagination: 'local',
      paginationSize: 10,
      paginationCounter: 'rows',
      movableColumns: true,
      resizableRows: true,
      tooltips: true,
      placeholder: 'Nenhum registro encontrado',
      index: 'id_manutencao'
    });

    const style = document.createElement('style');
    style.textContent = `
      .btn-save {
        background: #007bff;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-save:hover {
        background: #0056b3;
      }
      .btn-delete {
        background: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-delete:hover {
        background: #c82333;
      }
      .btn-back {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
  }, [dados, colunas]);

  useEffect(() => {
    if (apiStatus === 'connected' && tableRef.current) {
      if (!tabulatorRef.current) {
        inicializarTabulator();
      } else {
        tabulatorRef.current.setColumns(colunas);
        tabulatorRef.current.setData(dados);
      }
    }
  }, [dados, apiStatus, inicializarTabulator, colunas]);

  const adicionarRegistro = async (e) => {
    e.preventDefault();
    const { id_manutencao, id_escola, id_tp_manutencao, id_status, descr_manutencao, data_solicitacao, usuario_responsavel, nome_campanha, id_gerencia } = formData;

    // Validação de campos obrigatórios com mensagens específicas
    const camposObrigatorios = [];
    const erros = [];

    if (!id_manutencao) {
      camposObrigatorios.push('ID Manutenção');
      erros.push('id_manutencao');
    }

    if (!id_escola) {
      camposObrigatorios.push('Escola');
      erros.push('id_escola');
    }

    if (camposObrigatorios.length > 0) {
      setCamposComErro(erros);
      mostrarMensagem('error', `Por favor, preencha os campos obrigatórios: ${camposObrigatorios.join(', ')}`);
      return;
    }

    setCamposComErro([]);

    const dataToSend = {
      id_manutencao,
      id_escola,
      id_tp_manutencao,
      id_status,
      descr_manutencao,
      data_solicitacao,
      usuario_responsavel,
      nome_campanha,
      id_gerencia
    };

    logarRequisicao('Adicionar', 'manutenção', dataToSend);
    try {
      setLoading(true);
      const response = await api.post('/manutencao', dataToSend);
      logarSucesso('Adicionar', 'manutenção', response.data);
      setDados([...dados, response.data.data]);
      setFormData({ id_manutencao: '', id_escola: '', id_tp_manutencao: '', id_status: '', descr_manutencao: '', data_solicitacao: '', usuario_responsavel: '', nome_campanha: '', id_gerencia: '' });
      setCamposComErro([]);
      mostrarMensagem('success', 'Registro adicionado com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'adicionar', 'manutenção');
      mostrarMensagem('error', mensagemErro);
    }
    finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));

    // Remove o erro do campo quando ele for preenchido
    if (value || type === 'checkbox') {
      setCamposComErro(prev => prev.filter(campo => campo !== name));
    }
  };

  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download('csv', 'manutencao.csv');
    }
  };

  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => navigate('/')}><i className="fas fa-arrow-left"></i></button>
      </div>

      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>Gerenciador de Manutenção</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <p>Interface para popular e gerenciar a tabela tb_manutencao</p>
        <div style={{ marginTop: '10px' }}>
          <TutorialButton videoPath="bdados/manutencao.mov" buttonText="Ver Tutorial Manutenção" />
        </div>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
        </div>
      )}

      <div className="controls">
        <form onSubmit={adicionarRegistro} className="form-inline">
          <div className="form-group">
            <label htmlFor="id_manutencao">ID Manutenção:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input type="text" id="id_manutencao" name="id_manutencao" value={formData.id_manutencao} onChange={handleInputChange} placeholder="Ex: 1" disabled={loading || apiStatus !== 'connected'} className={camposComErro.includes('id_manutencao') ? 'input-error' : ''} />
          </div>
          <div className="form-group">
            <label htmlFor="id_escola">Escola:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="text"
              id="id_escola"
              name="id_escola"
              value={escolaSearch}
              onChange={(e) => {
                setEscolaSearch(e.target.value);
                const escolaEncontrada = escolas.find(esc => {
                  const formato = `${esc.nome_escola} (${esc.codigo_inep || esc.id_escola})`;
                  return formato === e.target.value || esc.nome_escola === e.target.value;
                });
                if (escolaEncontrada) {
                  setFormData(prev => ({ ...prev, id_escola: escolaEncontrada.id_escola }));
                  setCamposComErro(prev => prev.filter(c => c !== 'id_escola'));
                } else {
                  setFormData(prev => ({ ...prev, id_escola: '' }));
                }
              }}
              list="escolas-list-manutencao"
              placeholder="Digite para buscar escola..."
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('id_escola') ? 'input-error' : ''}
              autoComplete="off"
            />
            <datalist id="escolas-list-manutencao">
              {escolasFiltradas.map(item => (
                <option key={item.id_escola} value={`${item.nome_escola} (${item.codigo_inep || item.id_escola})`} />
              ))}
            </datalist>
          </div>
          <div className="form-group">
            <label htmlFor="id_tp_manutencao">Tipo manutenção:</label>
            <select id="id_tp_manutencao" name="id_tp_manutencao" value={formData.id_tp_manutencao} onChange={handleInputChange} disabled={loading || apiStatus !== 'connected'}>
              <option value="">Selecione um tipo</option>
              {tiposManutencao.map(item => (<option key={item.id_tp_manutencao} value={item.id_tp_manutencao}>{item.descr_tp_manutencao}</option>))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="id_status">Status:</label>
            <select id="id_status" name="id_status" value={formData.id_status} onChange={handleInputChange} disabled={loading || apiStatus !== 'connected'}>
              <option value="">Selecione um status</option>
              {status.map(item => (<option key={item.id_status} value={item.id_status}>{item.descr_status}</option>))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="descr_manutencao">Descrição manutenção:</label>
            <input type="text" id="descr_manutencao" name="descr_manutencao" value={formData.descr_manutencao} onChange={handleInputChange} placeholder="Ex: descrição" disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="data_solicitacao">Data solicitação:</label>
            <input type="date" id="data_solicitacao" name="data_solicitacao" value={formData.data_solicitacao} onChange={handleInputChange} disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="usuario_responsavel">Usuário responsável:</label>
            <input type="text" id="usuario_responsavel" name="usuario_responsavel" value={formData.usuario_responsavel} onChange={handleInputChange} placeholder="Ex: joao.silva" disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="nome_campanha">Nome campanha:</label>
            <input type="text" id="nome_campanha" name="nome_campanha" value={formData.nome_campanha} onChange={handleInputChange} placeholder="Ex: nome" disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="id_gerencia">Gerência:</label>
            <select id="id_gerencia" name="id_gerencia" value={formData.id_gerencia} onChange={handleInputChange} disabled={loading || apiStatus !== 'connected'}>
              <option value="">Selecione uma gerência</option>
              {gerencias.map(item => (<option key={item.id_gerencia} value={item.id_gerencia}>{item.nome_gerencia}</option>))}
            </select>
          </div>

          {/* Linha única: Adicionar, Recarregar, Exportar CSV + aviso de obrigatórios */}
          <div
            className="actions-row"
            style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'nowrap', marginTop: '10px' }}
          >
            <button type="submit" className="btn btn-success" disabled={loading || apiStatus !== 'connected'}>
              {loading ? '⏳ Adicionando...' : '➕ Adicionar'}
            </button>
            <button className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading || apiStatus !== 'connected'}>
              {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
            </button>
            <button className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>📥 Exportar CSV</button>

            <div className="info-campos-obrigatorios" style={{ whiteSpace: 'nowrap' }}>
              <i className="fas fa-info-circle"></i>
              <span style={{ marginLeft: 6 }}>* indica que os campos são obrigatorios.</span>
            </div>
          </div>
        </form>
      </div>

      <div className="stats-bar">
        <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
        <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
      </div>

      <div className="tabulator-container">
        <div ref={tableRef}></div>
      </div>

    </div>
  );
};

export default ManutencaoManager;
